import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './WorkDetail.css';
import { api } from '../../../api/client';
import type { Work } from '../../../api/types';
import { PageHeader } from '../../../components/PageHeader';
import { processUrls, ProcessedUrl } from '../utils/urlHelpers';
import LinkPreview from './LinkPreview';
import { parseWorkTitle } from '../utils/parseWorkTitle';

const WorkDetail: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [work, setWork] = useState<Work | null>(null);
    const [status, setStatus] = useState<'loading' | 'ready' | 'notfound' | 'error'>('loading');

    useEffect(() => {
        if (!slug) {
            setStatus('notfound');
            return;
        }
        let cancelled = false;
        api.works
            .get(slug)
            .then((row) => {
                if (cancelled) return;
                setWork(row);
                setStatus('ready');
            })
            .catch(() => {
                if (!cancelled) setStatus('notfound');
            });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    if (status === 'loading') return <div>Loading…</div>;
    if (status !== 'ready' || !work) return <div>Work not found</div>;

    const { mainTitle, subtitle } = parseWorkTitle(work.title);

    return (
        <div className="work-detail-page">
            <Helmet>
                <title>{work.title} - Lorenzo Saini</title>
                <meta name="description" content={work.description} />
                <link rel="canonical" href={`https://lorenzosaini.com/works/${slug}`} />
                <meta property="og:title" content={`${work.title} - Lorenzo Saini`} />
                <meta property="og:description" content={work.description} />
                <meta property="og:url" content={`https://lorenzosaini.com/works/${slug}`} />
                <meta property="og:image" content={`https://lorenzosaini.com${work.imageUrls[0]}`} />
                <meta property="og:type" content="article" />
                <meta name="twitter:title" content={`${work.title} - Lorenzo Saini`} />
                <meta name="twitter:description" content={work.description} />
                <meta name="twitter:image" content={`https://lorenzosaini.com${work.imageUrls[0]}`} />
            </Helmet>
            <PageHeader title="WORKS" />

            <div className="work-detail-card">
                <div className="work-hero">
                    <img src={work.imageUrls[0]} alt={work.title} />
                    <div className="work-hero-overlay">
                        <h1>
                            {mainTitle.split('').map((char, i) => {
                                // Check if character is Japanese (Hiragana, Katakana, or Kanji)
                                const isJapanese = /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\u4e00-\u9faf\u3400-\u4dbf]/.test(char);
                                return isJapanese ? (
                                    <span key={i} className="japanese-text">{char}</span>
                                ) : (
                                    <span key={i}>{char}</span>
                                );
                            })}
                            {subtitle && <span className="work-subtitle"> {subtitle}</span>}
                        </h1>
                        {work.date && <span className="work-date">{new Date(work.date).getFullYear()}</span>}
                    </div>
                </div>
                <div className="work-content">
                    <div className="work-description">
                        <p>{work.description}</p>
                    </div>
                    {work.urls && work.urls.length > 0 && (
                        <div className="work-media">
                            {processUrls(work.urls).map((processedUrl: ProcessedUrl, index: number) => {
                                const { type, url, embedUrl, displayName, iframeHtml } = processedUrl;

                                // Render raw iframe HTML
                                if (type === 'iframe' && iframeHtml) {
                                    return (
                                        <div
                                            key={index}
                                            dangerouslySetInnerHTML={{ __html: iframeHtml }}
                                            className="custom-iframe"
                                        />
                                    );
                                }

                                // Render embeddable content (YouTube, Vimeo)
                                if (embedUrl && (type === 'youtube' || type === 'vimeo')) {
                                    return (
                                        <div className="media-wrapper" key={index}>
                                            <iframe
                                                src={embedUrl}
                                                title={displayName}
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            ></iframe>
                                        </div>
                                    );
                                }

                                // Render link preview for website URLs
                                if (type === 'website') {
                                    return (
                                        <LinkPreview key={index} url={url} />
                                    );
                                }

                                // Render links for other platforms
                                return (
                                    <div className="media-link" key={index}>
                                        <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`platform-link platform-${type}`}
                                        >
                                            {displayName}
                                        </a>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WorkDetail;